import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type AnchorMap,
  buildInitialGeometry,
  buildTransitionGeometry,
  calcNewDeltas,
  G_TYPES,
  type GGeometry,
  geometryMotion,
} from './labelSprings';

/** Animates the gap labels towards `anchors`, one spring step per animation frame. */
export function useLabelSprings(anchors: AnchorMap | null): GGeometry | null {
  const [geometry, setGeometry] = useState<GGeometry | null>(null);

  // Changes only when an anchor moves, not on every timestamp tick, so scrubbing does not
  // restart the animation.
  const resetKey = useMemo(() => {
    if (anchors == null) return 'no-anchors';
    return G_TYPES.map(
      (type) => `${type}:${anchors[type][0].toFixed(4)},${anchors[type][1].toFixed(4)}`,
    ).join('|');
  }, [anchors]);

  const anchorsRef = useRef<AnchorMap | null>(null);
  anchorsRef.current = anchors;
  const prevGeomRef = useRef<GGeometry | null>(null);

  useEffect(() => {
    const currentAnchors = anchorsRef.current;
    if (currentAnchors == null) return;

    const prev = prevGeomRef.current;
    const startGeometry =
      prev != null
        ? buildTransitionGeometry(prev, currentAnchors)
        : buildInitialGeometry(currentAnchors);
    setGeometry(startGeometry);

    let rafId = 0;
    let running = true;

    const step = (geom: GGeometry) => {
      if (!running) return;
      const next = calcNewDeltas(geom);
      setGeometry(next);
      // Kept current mid-animation, so a cleanup still leaves the next transition a start.
      prevGeomRef.current = next;
      if (geometryMotion(next) > 0.003) {
        rafId = window.requestAnimationFrame(() => step(next));
      }
    };

    rafId = window.requestAnimationFrame(() => step(startGeometry));

    return () => {
      running = false;
      window.cancelAnimationFrame(rafId);
    };
    // Anchors are read through the ref; resetKey is value-equal while they do not move.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return geometry;
}
